import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";
import { ShoppingCart, Filter, Search, X, Eye, Zap, Tag, LayoutDashboard, IndianRupee } from 'lucide-react';
import { getFromLocalStorage, removeFromLocalStorage } from "../utils/localStorage";
import ProtectedRoute from "../Components/ProtectedRoute";
import { getAllCards, createTransaction, getTrendingCards } from '../api/apis';
import { processCashfreePayment } from '../utils/cashfree';

const RARITY_OPTIONS = [
  { value: 'Common', label: 'Common' },
  { value: 'Rare', label: 'Rare' },
  { value: 'Epic', label: 'Epic' },
  { value: 'Legendary', label: 'Legendary' },
  { value: 'Mythic', label: 'Mythic' }
];

const RARITY_STYLES = {
  'Common': {
    gradient: 'linear-gradient(135deg, #6B7280, #9CA3AF)',
    textColor: '#FFFFFF'
  },
  'Rare': {
    gradient: 'linear-gradient(135deg, #3B82F6, #60A5FA)',
    textColor: '#FFFFFF'
  },
  'Epic': {
    gradient: 'linear-gradient(135deg, #8B5CF6, #A78BFA)',
    textColor: '#FFFFFF'
  },
  'Legendary': {
    gradient: 'linear-gradient(135deg, #F59E0B, #FCD34D)',
    textColor: '#000000'
  },
  'Mythic': {
    gradient: 'linear-gradient(135deg, #EF4444, #F87171)',
    textColor: '#FFFFFF'
  }
};

const PurchaseButton = React.memo(({ card, onPurchase, isProcessing, onClick }) => {
  const handleClick = (e) => {
    e.stopPropagation();
    if (onClick) {
      onClick(e);
    }
    if (!isProcessing) {
      onPurchase(card);
    }
  };

  return (
    <div className="mt-6">
      <button
        onClick={handleClick}
        disabled={isProcessing}
        className="w-full py-3 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black rounded-lg font-semibold hover:from-yellow-500 hover:to-yellow-700 transition-all transform hover:scale-105 flex items-center justify-center space-x-2 shadow-lg shadow-yellow-500/25"
      >
        <ShoppingCart className="w-5 h-5" />
        <span>Purchase</span>
      </button>
    </div>
  );
});

const Dashboard = () => {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [filteredCards, setFilteredCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCard, setSelectedCard] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalCard, setModalCard] = useState(null);
  const [rarityFilter, setRarityFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [userData, setUserData] = useState('');
  const fetchedRef = useRef(false);
  const abortControllerRef = useRef(null);

  const categoryOptions = useMemo(() => {
    const categories = new Set();
    cards.forEach(card => {
      if (Array.isArray(card.category)) {
        card.category.forEach(cat => categories.add(cat));
      } else if (card.category) {
        categories.add(card.category);
      }
    });
    return Array.from(categories).map(cat => ({ value: cat, label: cat }));
  }, [cards]);

  const filteredCardsMemo = useMemo(() => {
    return cards.filter(card => {
      const matchesSearch = card.cardTitle?.toLowerCase().includes(searchQuery?.toLowerCase() || '');
      const matchesRarity = !rarityFilter || card.rarity === rarityFilter;
      const matchesCategory = !categoryFilter ||
        (Array.isArray(card.category)
          ? card.category.some(cat => cat === categoryFilter)
          : card.category === categoryFilter);
      return matchesSearch && matchesRarity && matchesCategory;
    });
  }, [cards, searchQuery, rarityFilter, categoryFilter]);

  useEffect(() => {
    setFilteredCards(filteredCardsMemo);
  }, [filteredCardsMemo]);

  const checkTimeout = useCallback(() => {
    const expirationTime = getFromLocalStorage("expirationTime");
    if (!expirationTime || Date.now() > parseInt(expirationTime, 10)) {
      return false;
    }
    return true;
  }, []);

  const handleLogout = useCallback(() => {
    removeFromLocalStorage("token");
    removeFromLocalStorage("user");
    removeFromLocalStorage("expirationTime");
    toast.error("Session expired. Please log in again.");
    window.location.reload();
  }, []);

  const fetchCards = useCallback(async () => {
    if (fetchedRef.current) return;

    if (!checkTimeout()) {
      handleLogout();
      return;
    }

    setLoading(true);
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const token = getFromLocalStorage("token");
      const user = getFromLocalStorage("user");

      if (!token) {
        // Temporarily disable this error during OTP verification
        if (!window.__suppressAuthChecks) {
          toast.error("No token found. Please log in again.");
          navigate("/login");
        }
        return;
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "User-Info": user?.emailId || user?.username || user
        },
        signal: controller.signal
      };

      // Only fetch trending cards (admin-added) for Dashboard
      const response = await getTrendingCards(config);

      if (response?.data?.status === "success") {
        // Filter to only show cards marked as trending
        const trendingCards = response.data.cards?.filter(card => card.isTrending === true) || [];
        setCards(trendingCards);
      } else {
        console.warn('No trending cards found, response:', response);
        setCards([]);
      }
    } catch (error) {
      if (error.name !== 'AbortError' && !controller.signal.aborted) {
        console.error('Error fetching cards:', error);
        if (error.response?.status === 401) {
          handleLogout();
        } else if (!error.message?.includes('canceled')) {
          toast.error('Failed to load cards. Please try again.');
        }
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [navigate, checkTimeout, handleLogout]);

  const handlePurchase = useCallback(async (card) => {
    console.log('🛒 DASHBOARD: Purchase initiated for card:', card.cardTitle);
    console.log('📧 DASHBOARD: Streamer Email ID:', card.streamerEmailId);

    if (!checkTimeout()) {
      handleLogout();
      return;
    }

    setSelectedCard(card);
    setShowModal(true);
    setIsProcessingPayment(true);

    try {
      const token = getFromLocalStorage("token");
      const user = getFromLocalStorage("user");

      console.log('👤 DASHBOARD: User:', user);
      console.log('🔑 DASHBOARD: Token exists:', !!token);

      if (!token) {
        toast.error("Authentication required");
        navigate("/login");
        return;
      }

      // Check if card is free
      const price = Number(card.price);

      if (price === 0) {
        console.log('🎁 DASHBOARD: Free card - creating transaction directly');
        const transactionData = {
          cardId: card._id,
          streamerEmailId: card.streamerEmailId,
          userEmailId: user?.emailId || user?.email,
          title: card.cardTitle,
          description: card.description,
          purchaseSource: 'dashboard',
          paymentId: 'free',
          orderId: 'free'
        };

        console.log('📦 DASHBOARD: Transaction data:', transactionData);

        const transactionResponse = await createTransaction(transactionData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Info": user?.emailId || user?.username || user
          }
        });

        console.log('✅ DASHBOARD: Transaction response:', transactionResponse.data);

        if (transactionResponse.data.status === "success") {
          console.log('🎉 DASHBOARD: Transaction created successfully!');
          toast.success("Card purchased successfully!");
          setIsProcessingPayment(false);
          setShowModal(false);
          // Redirect to My Cards for dashboard purchases
          setTimeout(() => {
            navigate('/my-cards');
          }, 800);
        } else {
          console.error('❌ DASHBOARD: Transaction failed:', transactionResponse.data);
          toast.error('Failed to create transaction');
          setIsProcessingPayment(false);
          setShowModal(false);
        }
        return;
      }

      console.log('💰 DASHBOARD: Paid card - starting Cashfree payment...');

      await processCashfreePayment(
        card,
        async (data) => {
          try {
            console.log('💳 DASHBOARD: Payment successful, creating transaction...');
            const transactionData = {
              cardId: card._id,
              streamerEmailId: card.streamerEmailId,
              userEmailId: user?.emailId || user?.email,
              title: card.cardTitle,
              description: card.description,
              purchaseSource: 'dashboard',
              paymentId: data.payment_id || 'cashfree_payment',
              orderId: data.order_id || `order_${Date.now()}`
            };

            console.log('📦 DASHBOARD: Transaction data:', transactionData);

            const transactionResponse = await createTransaction(transactionData, {
              headers: {
                Authorization: `Bearer ${token}`,
                "User-Info": user?.emailId || user?.username || user
              }
            });

            console.log('✅ DASHBOARD: Transaction response:', transactionResponse.data);

            if (transactionResponse.data.status === "success") {
              toast.success("Card purchased successfully!");
              console.log('🎉 DASHBOARD: Transaction created successfully!');
              // Redirect to My Cards
              navigate('/my-cards');
            } else {
              console.error('❌ DASHBOARD: Transaction failed:', transactionResponse.data);
              throw new Error('Transaction creation failed');
            }
          } catch (error) {
            console.error('❌ DASHBOARD: Transaction creation error:', error);
            toast.error("Payment successful but failed to record transaction. Please contact support.");
          } finally {
            setIsProcessingPayment(false);
            setShowModal(false);
          }
        },
        (error) => {
          console.error('❌ DASHBOARD: Payment error:', error);
          toast.error(error.message || "Payment failed. Please try again.");
          setIsProcessingPayment(false);
          setShowModal(false);
        }
      );
    } catch (error) {
      console.error('❌ DASHBOARD: Purchase error:', error);
      toast.error(error.message || "Failed to initiate purchase. Please try again.");
      setIsProcessingPayment(false);
      setShowModal(false);
    }
  }, [checkTimeout, handleLogout, navigate, fetchCards]);

  const selectStyles = useMemo(() => ({
    control: (provided) => ({
      ...provided,
      backgroundColor: '#374151',
      borderColor: '#4B5563',
      minHeight: '42px',
      '&:hover': {
        borderColor: '#EAB308'
      }
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#374151',
      border: '1px solid #4B5563'
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? '#4B5563' : '#374151',
      color: 'white',
      '&:hover': {
        backgroundColor: '#4B5563'
      }
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#4B5563'
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: 'white'
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: 'white',
      '&:hover': {
        backgroundColor: '#EF4444',
        color: 'white'
      }
    }),
    input: (provided) => ({
      ...provided,
      color: 'white',
      backgroundColor: '#374151'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9ca3af'
    })
  }), []);

  const handleOpenCardModal = (card) => {
    setModalCard(card);
  };

  const handleCloseCardModal = () => {
    setModalCard(null);
  };

  const CardItem = useCallback(({ card }) => {
    const rarityStyle = RARITY_STYLES[card.rarity] || RARITY_STYLES['Common'];

    return (
      <div className="bg-black/30 rounded-xl p-6 border border-gray-800 backdrop-blur-sm group hover:border-yellow-500/50 transition-all duration-300 cursor-pointer" onClick={() => handleOpenCardModal(card)}>
        <div className="flex justify-between items-start mb-4">
          <div
            className="px-3 py-1 rounded-full text-xs font-semibold flex items-center space-x-1"
            style={{
              background: rarityStyle.gradient,
              color: rarityStyle.textColor
            }}
          >
            <span>{card.rarity}</span>
          </div>
          <div className="flex items-center space-x-1 text-yellow-400">
            <IndianRupee className="w-4 h-4" />
            <span>{card.price}</span>
          </div>
        </div>

        <div className="mb-4 relative overflow-hidden rounded-lg bg-gray-800/50">
          <img
            src={card.imageUrl || card.cardImage || "https://picsum.photos/300/200"}
            alt={card.cardTitle}
            className="w-full h-80 object-contain bg-black transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
            onError={(e) => { e.target.src = "https://picsum.photos/300/200"; }}
          />
        </div>

        <div className="space-y-3">
          <PurchaseButton
            card={card}
            onPurchase={handlePurchase}
            isProcessing={isProcessingPayment && selectedCard?._id === card._id}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </div>
    );
  }, [handlePurchase, isProcessingPayment, selectedCard]);

  useEffect(() => {
    const user = getFromLocalStorage('user');
    if (user) {
      setUserData(typeof user === 'string' ? user : user.username || user.emailId || 'User');
    }

    fetchCards();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchCards]);

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
        <main className="pt-6 pb-12 px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center">
                  <LayoutDashboard className="w-6 h-6 text-black" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
                    Trending Cards
                  </h1>
                  <p className="text-gray-400">Discover and purchase trending cards from popular creators</p>
                </div>
              </div>
            </div>

            <div className="bg-black/30 rounded-xl p-6 mb-8 border border-gray-800 backdrop-blur-sm">
              <div className="flex flex-col">
                <label className="block text-white text-sm font-medium mb-2">
                  Filter by Rarity
                </label>
                <select
                  value={rarityFilter}
                  onChange={(e) => setRarityFilter(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700/50 border border-gray-600 rounded-lg text-white outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-200"
                >
                  <option value="">All Rarities</option>
                  {RARITY_OPTIONS.map((rarity) => (
                    <option key={rarity.value} value={rarity.value}>
                      {rarity.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search cards by title..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-black/30 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors"
                  />
                </div>
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {[...Array(8)].map((_, index) => (
                  <div key={index} className="bg-black/30 rounded-xl p-6 border border-gray-800 backdrop-blur-sm animate-pulse">
                    <div className="flex justify-between items-start mb-4">
                      <div className="w-16 h-6 bg-gray-700 rounded-full"></div>
                      <div className="w-8 h-4 bg-gray-700 rounded"></div>
                    </div>
                    <div className="w-full h-48 bg-gray-700 rounded-lg mb-4"></div>
                    <div className="space-y-3">
                      <div className="h-6 bg-gray-700 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-700 rounded w-full"></div>
                      <div className="h-4 bg-gray-700 rounded w-2/3"></div>
                      <div className="flex justify-between items-center">
                        <div className="h-4 bg-gray-700 rounded w-1/3"></div>
                        <div className="h-4 bg-gray-700 rounded w-1/4"></div>
                      </div>
                      <div className="h-12 bg-gray-700 rounded w-full mt-4"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredCards.length === 0 ? (
              <div className="text-center py-20">
                <div className="text-gray-400 text-lg mb-4">
                  {searchQuery || rarityFilter || categoryFilter ? "No cards found matching your filters." : "No cards available."}
                </div>
                {(searchQuery || rarityFilter || categoryFilter) && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setRarityFilter('');
                      setCategoryFilter('');
                    }}
                    className="text-yellow-400 hover:text-yellow-300 transition-colors"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {filteredCards.map((card) => (
                  <CardItem
                    key={card._id || card.id || Math.random().toString(36).substring(2, 9)}
                    card={card}
                  />
                ))}
              </div>
            )}

            {showModal && selectedCard && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <div className="bg-gray-900 rounded-xl p-6 max-w-md w-full border border-gray-700">
                  <h3 className="text-xl font-bold text-white mb-4">
                    Processing Payment
                  </h3>
                  <p className="text-gray-400 mb-4">
                    Please complete the payment for "{selectedCard.cardTitle}"
                  </p>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Card Detail Modal */}
            {modalCard && (
              <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={handleCloseCardModal}>
                <div className="relative bg-gradient-to-br from-gray-900 via-black to-gray-900 border border-yellow-500/30 rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
                  <button className="absolute top-3 right-3 text-yellow-300 hover:text-yellow-200 text-2xl font-bold w-8 h-8 flex items-center justify-center" onClick={handleCloseCardModal}>
                    ×
                  </button>
                  <div className="grid md:grid-cols-2 gap-0">
                    <div className="relative h-80 md:h-full">
                      <img
                        src={modalCard.imageUrl || modalCard.cardImage || "https://picsum.photos/300/200"}
                        alt={modalCard.cardTitle}
                        className="w-full h-full object-contain bg-black"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 ring-1 ring-yellow-400/40 rounded-none"></div>
                      <div className="absolute top-3 left-3">
                        <div
                          className="px-3 py-1 rounded-full text-xs font-semibold flex items-center space-x-1"
                          style={{
                            background: (RARITY_STYLES[modalCard.rarity] || RARITY_STYLES['Common']).gradient,
                            color: (RARITY_STYLES[modalCard.rarity] || RARITY_STYLES['Common']).textColor
                          }}
                        >
                          <span>{modalCard.rarity}</span>
                        </div>
                      </div>
                      {modalCard.price === 0 && (
                        <div className="absolute top-3 right-3 bg-green-500/90 text-white px-2 py-1 rounded-full text-xs font-bold">
                          FREE
                        </div>
                      )}
                    </div>
                    <div className="p-6 space-y-4">
                      <h3 className="text-2xl font-bold text-yellow-300">{modalCard.cardTitle}</h3>
                      <p className="text-gray-300 text-sm">{modalCard.description || modalCard.cardDescription || "No description available"}</p>

                      <div className="space-y-3">
                        <div className="flex items-center space-x-2 text-lg font-bold text-yellow-300">
                          <IndianRupee className="w-4 h-4 text-yellow-400" />
                          <span>{modalCard.price === 0 ? 'FREE' : modalCard.price}</span>
                        </div>
                      </div>

                      <PurchaseButton
                        card={modalCard}
                        onPurchase={handlePurchase}
                        isProcessing={isProcessingPayment && selectedCard?._id === modalCard._id}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>
        </main>
      </div>
    </ProtectedRoute>
  );
};

export default Dashboard;
